// The trees of the 3D outskirts, built the way games build foliage: not solid shapes but
// many textured cards. A spruce is whorls of drooping, needled branch fronds round a dark
// core, tapering to a spire; a broadleaf tree is a forked trunk under a crown of leaf
// clumps bunched into lobes. Every vertex carries its own light and shade (dark inside
// and underneath, bright at the tips and the top), the cards are lit as if the crown were
// one soft mass, the leaves cut their own holes in the shadows, and the crowns sway a
// little in the wind. Textures are painted on canvases at load: nothing to download.
// Sizes are in tiles, for a tree of scale 1: about two and a quarter tiles tall.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** A seeded random stream, so every tree of a kind is the same shape every time. */
function stream(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const UP = new THREE.Vector3(0, 1, 0);
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

// ---- textures ------------------------------------------------------------------------
// Each foliage texture is 512 square: a strip along the top is solid foliage, for the
// crown's opaque core; below a gap, the rest holds one frond or one leaf clump.

const TEX = 512;
/** The core strip's rows, and where the card art starts. */
const CORE_ROWS = 64, ART_TOP = 80;
/** The texture v at the top of the card art, and the middle of the core strip. */
const ART_V = 1 - ART_TOP / TEX;
const CORE_UV: [number, number] = [0.5, 1 - CORE_ROWS / 2 / TEX];

function texture(draw: (g: CanvasRenderingContext2D) => void, wrap = false): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = wrap ? 128 : TEX;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  if (wrap) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

/** Fills the core strip with densely speckled foliage in the given colours. */
function coreStrip(g: CanvasRenderingContext2D, r: () => number, base: string, flecks: string[]): void {
  g.fillStyle = base;
  g.fillRect(0, 0, TEX, CORE_ROWS);
  for (let i = 0; i < 900; i++) {
    g.fillStyle = flecks[Math.floor(r() * flecks.length)]!;
    g.fillRect(r() * TEX, r() * CORE_ROWS, 2 + r() * 5, 1 + r() * 3);
  }
}

/** A spruce frond along u, from the trunk at u = 0 to the tip at u = 1: side twigs angled out toward the tip, thick with needles. */
function needleTexture(): THREE.CanvasTexture {
  const r = stream(501);
  return texture(g => {
    coreStrip(g, r, '#1b2d1c', ['#14231a', '#223823', '#28402a', '#1e3320']);
    const cy = (ART_TOP + TEX) / 2, half = (TEX - ART_TOP) / 2 - 8;
    // the frond's half-width along it: narrow at the trunk, fullest a third of the way out, to a point at the tip
    const width = (u: number) => half * Math.pow(Math.max(0, Math.sin(Math.PI * Math.min(1, 0.14 + u * 0.92))), 0.65);
    const deep = ['#25412a', '#2c4a31', '#325235'];
    const mid = ['#3a5e38', '#42693d', '#4a7142', '#3b6139'];
    const tip = ['#5c8650', '#688f56', '#76995e', '#83a466'];
    g.lineCap = 'round';
    for (let u = 0.02; u < 0.94; u += 0.03) {
      const w = width(u);
      if (w < 4) continue;
      for (const side of [-1, 1]) {
        const x0 = u * TEX, y0 = cy;
        const x1 = x0 + w * (0.55 + r() * 0.2), y1 = cy + side * w * (0.9 + r() * 0.1);
        const dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy), ax = dx / len, ay = dy / len;
        const steps = Math.max(4, Math.round(len / 6));
        for (let k = 0; k <= steps; k++) {
          const f = k / steps, px = x0 + dx * f, py = y0 + dy * f;
          // needles splay forward off both sides of the twig, lighter toward its end and toward the frond's tip
          const shade = f * 0.7 + u * 0.5 + (r() - 0.5) * 0.5;
          const pal = shade > 0.85 ? tip : shade > 0.35 ? mid : deep;
          for (const s of [-1, 1]) {
            const a = Math.atan2(ay, ax) + s * (0.75 + r() * 0.4);
            const l = (5 + r() * 6) * (1 - f * 0.35);
            g.strokeStyle = pal[Math.floor(r() * pal.length)]!;
            g.lineWidth = 1.6 + r() * 1.2;
            g.beginPath();
            g.moveTo(px, py);
            g.lineTo(px + Math.cos(a) * l, py + Math.sin(a) * l);
            g.stroke();
          }
        }
        g.strokeStyle = '#3a3324';
        g.lineWidth = 1.4;
        g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke();
      }
    }
    // the branch itself, thick at the trunk
    for (let x = 0; x < TEX * 0.93; x += 4) {
      g.strokeStyle = '#3b3123';
      g.lineWidth = 5 * (1 - x / TEX) + 1;
      g.beginPath(); g.moveTo(x, cy); g.lineTo(x + 5, cy); g.stroke();
    }
  });
}

/** The leaf-clump card spans this much of the texture's width (the art is square). */
const LEAF_U0 = (TEX - (TEX - ART_TOP)) / 2 / TEX, LEAF_U1 = 1 - LEAF_U0;

/** A clump of leaves on a few twigs: ragged at the edge, darker underneath and in the middle. */
function leafTexture(): THREE.CanvasTexture {
  const r = stream(733);
  return texture(g => {
    coreStrip(g, r, '#22341a', ['#1a2a15', '#2a3f1f', '#304623', '#1e3018']);
    const cx = TEX / 2, cy = (ART_TOP + TEX) / 2, R = (TEX - ART_TOP) / 2 - 14;
    // twigs from the middle out
    g.strokeStyle = '#3d3326';
    g.lineCap = 'round';
    for (let i = 0; i < 7; i++) {
      const a = r() * Math.PI * 2, l = R * (0.5 + r() * 0.4);
      g.lineWidth = 3;
      g.beginPath(); g.moveTo(cx, cy); g.lineTo(cx + Math.cos(a) * l, cy + Math.sin(a) * l); g.stroke();
    }
    // leaves, back to front: the shaded ones first, the sunlit ones over them
    const leaves: { x: number; y: number; a: number; l: number; light: number }[] = [];
    for (let i = 0; i < 520; i++) {
      const a = r() * Math.PI * 2, d = (R - 10) * Math.pow(r(), 0.5);
      const x = cx + Math.cos(a) * d, y = cy + Math.sin(a) * d;
      // lit from the top of the card, and more at the rim than in the clump's depths
      const light = 0.45 * (1 - (y - cy + R) / (2 * R)) + 0.35 * (d / R) + 0.4 * r();
      leaves.push({ x, y, a: r() * Math.PI, l: 17 + r() * 13, light });
    }
    leaves.sort((p, q) => p.light - q.light);
    for (const p of leaves) {
      const hue = 80 + r() * 26, sat = 30 + r() * 18, lit = 17 + p.light * 30;
      g.fillStyle = `hsl(${hue},${sat}%,${lit}%)`;
      g.beginPath();
      g.ellipse(p.x, p.y, p.l / 2, p.l * (0.22 + r() * 0.12), p.a, 0, Math.PI * 2);
      g.fill();
      // a pale midrib on the brighter leaves
      if (p.light > 0.7) {
        g.strokeStyle = `hsla(${hue},${sat}%,${lit + 12}%,0.6)`;
        g.lineWidth = 1.5;
        g.beginPath();
        g.moveTo(p.x - Math.cos(p.a) * p.l * 0.4, p.y - Math.sin(p.a) * p.l * 0.4);
        g.lineTo(p.x + Math.cos(p.a) * p.l * 0.4, p.y + Math.sin(p.a) * p.l * 0.4);
        g.stroke();
      }
    }
  });
}

/** Grey-brown bark in rough vertical plates, tinted per tree. */
function barkTexture(): THREE.CanvasTexture {
  const r = stream(919);
  return texture(g => {
    g.fillStyle = '#8a7a68';
    g.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 160; i++) {
      const v = 90 + r() * 70;
      g.fillStyle = `rgba(${v},${v * 0.88},${v * 0.75},0.6)`;
      g.fillRect(r() * 128, r() * 128, 2 + r() * 5, 10 + r() * 40);
    }
    g.fillStyle = 'rgba(30,24,18,0.7)';
    for (let i = 0; i < 40; i++) g.fillRect(r() * 128, r() * 128, 1 + r() * 1.5, 14 + r() * 50);
  }, true);
}

// ---- materials -----------------------------------------------------------------------

/** Seconds of wind: the outskirts move it on every frame. */
export const treeWind = { value: 0 };

/**
 * Foliage: alpha-tested cards seen from both sides, shaded with their own (crown-shaped)
 * normals on both faces rather than flipped on the back, and swaying more toward the top.
 */
function foliage(map: THREE.Texture): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ map, alphaTest: 0.5, side: THREE.DoubleSide, vertexColors: true });
  m.onBeforeCompile = s => {
    s.uniforms.uWind = treeWind;
    s.vertexShader = 'uniform float uWind;\n' + s.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
      #ifdef USE_INSTANCING
        vec3 root = instanceMatrix[3].xyz;
      #else
        vec3 root = vec3(0.0);
      #endif
      float sway = max(position.y - 0.5, 0.0);
      float ph = uWind * 1.3 + root.x * 0.31 + root.z * 0.23;
      transformed.x += sway * 0.03 * (sin(ph) + 0.35 * sin(ph * 2.9 + position.z * 4.0));
      transformed.z += sway * 0.022 * sin(ph * 0.83 + 1.7 + position.x * 4.0);`);
    s.fragmentShader = s.fragmentShader.replace('#include <normal_fragment_begin>',
      THREE.ShaderChunk.normal_fragment_begin.replace(/normal \*= faceDirection;/, ''));
  };
  return m;
}

/**
 * For a stand-in that only casts a shadow: three has no such switch, so in the main pass it
 * throws every vertex off screen (no pixels drawn), while the shadow pass, which copies the
 * map, alpha test and side from it, draws its leaves into the shadow map as usual.
 */
function shadowOnly(map: THREE.Texture): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ map, alphaTest: 0.5 });
  m.shadowSide = THREE.DoubleSide;
  m.onBeforeCompile = s => {
    s.vertexShader = s.vertexShader.replace('#include <fog_vertex>', '#include <fog_vertex>\n  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);');
  };
  return m;
}

export interface TreeMaterials {
  needles: THREE.Material; leaves: THREE.Material; bark: THREE.Material;
  /** The shadow-only stand-ins' materials (see shadowOnly). */
  needleShadow: THREE.Material; leafShadow: THREE.Material;
}

export function treeMaterials(): TreeMaterials {
  const needles = needleTexture(), leaves = leafTexture();
  return {
    needles: foliage(needles), leaves: foliage(leaves), bark: new THREE.MeshLambertMaterial({ map: barkTexture() }),
    needleShadow: shadowOnly(needles), leafShadow: shadowOnly(leaves),
  };
}

// ---- shapes --------------------------------------------------------------------------

/** Gathers vertices (position, normal, uv, grey shade) and triangles into one geometry. */
class Mesher {
  private readonly pos: number[] = [];
  private readonly nor: number[] = [];
  private readonly uv: number[] = [];
  private readonly col: number[] = [];
  private readonly idx: number[] = [];

  vert(p: THREE.Vector3, n: THREE.Vector3, u: number, v: number, shade: number): number {
    this.pos.push(p.x, p.y, p.z);
    this.nor.push(n.x, n.y, n.z);
    this.uv.push(u, v);
    this.col.push(shade, shade, shade);
    return this.pos.length / 3 - 1;
  }

  quad(a: number, b: number, c: number, d: number): void {
    this.idx.push(a, b, c, a, c, d);
  }

  /** Adds a ready-made shape textured with the core strip, its normals and shades worked out per vertex. */
  solid(geo: THREE.BufferGeometry, normal: (p: THREE.Vector3, n: THREE.Vector3) => THREE.Vector3, shade: (p: THREE.Vector3) => number): void {
    const g = geo;
    const pos = g.attributes.position!, nor = g.attributes.normal!;
    const base = this.pos.length / 3;
    const p = new THREE.Vector3(), n = new THREE.Vector3();
    for (let k = 0; k < pos.count; k++) {
      p.fromBufferAttribute(pos, k);
      n.fromBufferAttribute(nor, k);
      this.vert(p, normal(p, n), CORE_UV[0], CORE_UV[1], shade(p));
    }
    if (g.index) for (let k = 0; k < g.index.count; k++) this.idx.push(base + g.index.getX(k));
    else for (let k = 0; k < pos.count; k++) this.idx.push(base + k);
  }

  build(): THREE.BufferGeometry {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    geo.setIndex(this.idx);
    geo.computeBoundingSphere();
    return geo;
  }
}

/** Where a pine's needle pads sit, and how wide they are: high on the trunk, uneven, smaller to the top. */
const PADS: [number, number, number, number][] = (() => {
  const r = stream(61);
  const pads: [number, number, number, number][] = [[0.04, 2.25, -0.02, 0.32]];
  for (let i = 0; i < 9; i++) {
    const a = i * 2.4 + (r() - 0.5) * 0.6, t = i / 8;
    const d = 0.12 + r() * 0.3;
    // broad, flat pads low in the crown, smaller ones toward the top
    pads.push([Math.cos(a) * d, 1.15 + t * 0.95 + (r() - 0.5) * 0.12, Math.sin(a) * d, 0.55 - t * 0.2 + r() * 0.06]);
  }
  return pads;
})();
const PINE_MID = new THREE.Vector3(0, 1.7, 0);

/** One needled frond out from `o` along `d`, sagging toward its tip and rolled so it is never seen edge-on. */
function frond(m: Mesher, o: THREE.Vector3, d: THREE.Vector3, len: number, droop: number, w: number, roll: number,
  normal: (q: THREE.Vector3) => THREE.Vector3, shade: (u: number, q: THREE.Vector3) => number, rows: number[]): void {
  const side = new THREE.Vector3(-d.z, 0, d.x).normalize().multiplyScalar(Math.cos(roll)).addScaledVector(UP, Math.sin(roll));
  const c = new THREE.Vector3(), p = new THREE.Vector3();
  const ids = rows.map(u => {
    c.copy(o).addScaledVector(d, len * u);
    c.y -= droop * Math.pow(u, 1.7);
    const s = shade(u, c);
    return [
      m.vert(p.copy(c).addScaledVector(side, -w), normal(c), u, 0, s),
      m.vert(p.copy(c).addScaledVector(side, w), normal(c), u, ART_V, s),
    ];
  });
  for (let i = 0; i + 1 < ids.length; i++) m.quad(ids[i]![0]!, ids[i + 1]![0]!, ids[i + 1]![1]!, ids[i]![1]!);
}

/**
 * A pine's crown: no cone, but a handful of flat, ragged pads of needles held out on limbs
 * high up a bare trunk, the way old Scots pines grow. `far` is the cheap version for woods
 * only ever seen from far off.
 */
export function pineCrown(far: boolean): THREE.BufferGeometry {
  const r = stream(far ? 41 : 17);
  const m = new Mesher();
  const nrm = new THREE.Vector3(), out = new THREE.Vector3(), d = new THREE.Vector3();
  /** Lit as one soft mass: out from the crown's middle and up. */
  const normal = (q: THREE.Vector3) => nrm.copy(q).sub(PINE_MID).normalize().multiplyScalar(0.6).addScaledVector(UP, 0.7).normalize();
  for (const [px, py, pz, rad] of PADS) {
    const centre = new THREE.Vector3(px, py, pz);
    const height = (q: THREE.Vector3) => 0.78 + 0.32 * Math.min(1, Math.max(0, (q.y - 1.3) / 1.0));
    // a dark, flattened heart to each pad, so it is never see-through
    const core = new THREE.IcosahedronGeometry(rad * 0.72, 0).scale(1, 0.5, 1);
    core.translate(px, py, pz);
    m.solid(core, q => normal(q), q => 0.5 * height(q));
    // fronds fanned out round the pad, a few tilted up over its top to round it off
    const count = far ? 4 : 10;
    const a0 = r() * Math.PI * 2;
    for (let j = 0; j < count; j++) {
      const a = a0 + (j / count) * Math.PI * 2 + (r() - 0.5) * 0.7;
      // nearly flat, a few lifted over the top to round the pad into a cloud of needles
      const up = j % 4 === 0 ? 0.3 + r() * 0.2 : (r() - 0.5) * 0.18;
      d.set(Math.cos(a), up, Math.sin(a)).normalize();
      const len = rad * (0.85 + r() * 0.35);
      const o = centre.clone().addScaledVector(d, -len * 0.45);
      frond(m, o, d, len * (far ? 1.2 : 1.05), len * (0.08 + r() * 0.1), (len * 0.6 + 0.04) * (far ? 1.35 : 1),
        (j % 2 ? 1 : -1) * (0.3 + r() * 0.4), normal,
        (u, q) => (0.62 + 0.42 * u) * height(q) * (0.85 + 0.3 * Math.min(1, out.copy(q).sub(PINE_MID).length() / 0.7)),
        far ? [0, 1] : [0, 0.5, 1]);
    }
  }
  return m.build();
}

/** Where a broadleaf crown's lobes sit, and how big they are. */
const LOBES: [number, number, number, number][] = (() => {
  const r = stream(23);
  const lobes: [number, number, number, number][] = [[0, 1.35, 0, 0.5], [0.05, 1.8, -0.04, 0.36]];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + (r() - 0.5) * 0.7, d = 0.36 + r() * 0.12;
    lobes.push([Math.cos(a) * d, 1.12 + r() * 0.42, Math.sin(a) * d, 0.3 + r() * 0.12]);
  }
  return lobes;
})();
const CROWN_MID = new THREE.Vector3(0, 1.4, 0);

/** A broadleaf tree's crown: leaf clumps bunched into lobes round a dark, solid heart. */
export function leafyCrown(far: boolean): THREE.BufferGeometry {
  const r = stream(far ? 59 : 29);
  const m = new Mesher();
  const out = new THREE.Vector3(), nrm = new THREE.Vector3(), cn = new THREE.Vector3(), t1 = new THREE.Vector3(), t2 = new THREE.Vector3(), p = new THREE.Vector3(), dir = new THREE.Vector3();
  /** Crown-shaped lighting: out from the crown's middle, tipped up. */
  const crownNormal = (q: THREE.Vector3) => nrm.copy(q).sub(CROWN_MID).normalize().multiplyScalar(0.75).addScaledVector(UP, 0.5).normalize();
  /** Dark low down and in the middle, bright at the top and the rim. */
  const crownShade = (q: THREE.Vector3) => 0.62 + 0.38 * clamp01((q.y - 0.85) / 1.2) + 0.25 * Math.min(1, out.copy(q).sub(CROWN_MID).length() / 0.8);
  // far off, one dark heart for the whole crown instead of one per lobe
  if (far) {
    const core = new THREE.IcosahedronGeometry(0.55, 0).scale(1.05, 0.85, 1.05);
    core.translate(CROWN_MID.x, CROWN_MID.y - 0.05, CROWN_MID.z);
    m.solid(core, q => crownNormal(q), q => crownShade(q) * 0.75);
  }
  for (const [lx, ly, lz, rad] of LOBES) {
    const centre = new THREE.Vector3(lx, ly, lz);
    if (!far) {
      const core = new THREE.IcosahedronGeometry(rad * 0.6, 0);
      core.translate(lx, ly, lz);
      m.solid(core, q => crownNormal(q), q => crownShade(q) * 0.75);
    }
    const cards = far ? (rad > 0.4 ? 3 : 2) : 9;
    for (let k = 0; k < cards; k++) {
      // spread over the lobe, more on top than underneath
      dir.set(r() * 2 - 1, r() * 2 - 0.7, r() * 2 - 1).normalize();
      const at = new THREE.Vector3().copy(centre).addScaledVector(dir, rad * (0.7 + r() * 0.25));
      // facing out of the lobe, give or take
      cn.copy(dir).add(t1.set(r() - 0.5, r() - 0.5, r() - 0.5).multiplyScalar(1.1)).normalize();
      t1.set(0, 1, 0).cross(cn);
      if (t1.lengthSq() < 0.01) t1.set(1, 0, 0);
      t1.normalize().applyAxisAngle(cn, r() * Math.PI * 2);
      t2.copy(cn).cross(t1).normalize();
      const s = rad * (far ? 1.85 : 1.25) * (0.85 + r() * 0.3) / 2;
      const ids: number[] = [];
      for (const [a, b, u, v] of [[-1, -1, LEAF_U0, 0], [1, -1, LEAF_U1, 0], [1, 1, LEAF_U1, ART_V], [-1, 1, LEAF_U0, ART_V]] as const) {
        p.copy(at).addScaledVector(t1, a * s).addScaledVector(t2, b * s);
        ids.push(m.vert(p, crownNormal(p), u, v, crownShade(p)));
      }
      m.quad(ids[0]!, ids[1]!, ids[2]!, ids[3]!);
    }
  }
  return m.build();
}

/** A tapering round limb from a to b. */
function limb(a: THREE.Vector3, b: THREE.Vector3, r0: number, r1: number, sides = 5): THREE.BufferGeometry {
  const len = a.distanceTo(b);
  const g = new THREE.CylinderGeometry(r1, r0, len, sides, 1, true);
  g.translate(0, len / 2, 0);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(UP, new THREE.Vector3().subVectors(b, a).normalize()));
  g.translate(a.x, a.y, a.z);
  return g;
}

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** A pine's trunk: tall, bare and a little crooked, with a limb out to each pad of needles. */
export function pineTrunk(): THREE.BufferGeometry {
  const bend = V(0.05, 1.05, -0.03), top = V(0.02, 2.15, 0);
  const parts = [limb(V(0, 0, 0), bend, 0.09, 0.07, 6), limb(bend, top, 0.07, 0.025, 6)];
  for (const [x, y, z] of PADS.slice(1)) {
    const from = V(bend.x * (1 - (y - 1.05) / 1.1) + top.x * ((y - 1.05) / 1.1), y - 0.18, 0);
    parts.push(limb(from, V(x * 0.85, y - 0.04, z * 0.85), 0.035, 0.015, 4));
  }
  return mergeGeometries(parts)!;
}

/** A broadleaf trunk, forking into limbs that reach up into the crown's lobes. */
export function leafyTrunk(): THREE.BufferGeometry {
  const fork = V(0.02, 0.72, 0);
  const parts = [limb(V(0, 0, 0), fork, 0.1, 0.072, 6)];
  for (const [x, y, z, rad] of LOBES.slice(2)) parts.push(limb(fork, V(x * 0.8, y - rad * 0.2, z * 0.8), 0.05, 0.018));
  parts.push(limb(fork, V(0.03, 1.6, -0.02), 0.055, 0.02));
  return mergeGeometries(parts)!;
}

/** A plain trunk for trees seen only from far off, up into the crown, with no limbs. */
export function farTrunk(): THREE.BufferGeometry {
  return limb(V(0, 0, 0), V(0.02, 1.7, 0), 0.08, 0.03, 5);
}

/** A dead, burnt tree: a snapped trunk with a few broken-off branch stubs. One tile tall: scaled per tree. */
export function snag(): THREE.BufferGeometry {
  const r = stream(83);
  const parts = [limb(V(0, 0, 0), V(0.02, 1, 0), 0.085, 0.035, 6)];
  for (let i = 0; i < 4; i++) {
    const y = 0.4 + i * 0.14 + r() * 0.06, a = r() * Math.PI * 2, l = 0.12 + r() * 0.16;
    parts.push(limb(V(0, y, 0), V(Math.cos(a) * l, y + l * (0.3 + r() * 0.5), Math.sin(a) * l), 0.025, 0.008, 4));
  }
  return mergeGeometries(parts)!;
}
